<script setup>
import { computed, defineProps, defineEmits, nextTick, watch } from 'vue';
import RadioButton from './../ui/RadioButton.vue';
import CheckBox from './../ui/CheckBox.vue';

const props = defineProps({
  item: Object,
  modelValue: [String, Array, Number],
  activeIndex: Number,
  displayIndex: Number,
  popupsData: Object,
  continueDisabled: {
    type: Boolean,
    default: false
  },
  isLastStep: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'continue', 'next-slide', 'show-popup']);

const NONE_NEED = 'Пропустить';

const checkboxModel = computed({
  get: () => (Array.isArray(props.modelValue) ? props.modelValue : []),
  set: (val) => emit('update:modelValue', val),
});

const normalizedOptions = computed(() => {
  const opts = props.item?.options || [];

  const mapped = opts.map((opt) => ({
    ...opt,
    __value: (opt?.value !== undefined && opt?.value !== null) ? opt.value : opt?.name,
  }));

  if (props.item?.value === 'textures') {
    const hasSkip = mapped.some((opt) => String(opt.__value) === NONE_NEED);

    if (!hasSkip) {
      mapped.push({
        name: NONE_NEED,
        value: NONE_NEED,
        __value: NONE_NEED,
      });
    }
  }

  return mapped;
});

const filteredOptions = computed(() => {
  if (props.item.type !== 'checkbox') return normalizedOptions.value;

  const hasSelectedRealOption = checkboxModel.value?.some(
      (v) => v !== NONE_NEED
  );

  return normalizedOptions.value.filter((opt) => {
    if (opt.__value === NONE_NEED && hasSelectedRealOption) {
      return false;
    }
    return true;
  });
});

const handleRadioChange = async (val) => {
  emit('update:modelValue', val);
  await nextTick();
  emit('next-slide', props.isLastStep, props.item);
};

const onContinue = () => emit('continue', props.isLastStep, props.item);

const showContinueButton = computed(() => {
  return props.item?.type === 'checkbox'
      && Array.isArray(checkboxModel.value)
      && checkboxModel.value.length > 0;
});

const handleDescriptionClick = (event) => {
  const el = event.target.closest('[data-popup-id]');
  if (!el) return;

  const popupId = el.dataset.popupId;
  if (popupId && props.popupsData?.[popupId]) {
    emit('show-popup', props.popupsData[popupId]);
  } else {
    console.error(`Popup content not found for ID: ${popupId}`);
  }
};

watch(
    () => checkboxModel.value,
    (val) => {
      if (!Array.isArray(val)) return;

      const hasReal = val.some((v) => v !== NONE_NEED);

      if (hasReal && val.includes(NONE_NEED)) {
        checkboxModel.value = val.filter((v) => v !== NONE_NEED);
      }
    },
    { deep: true }
);
</script>

<template>
  <div class="feed-picker-slider__item">
    <div class="feed-picker-slider__item-num">
      {{ (displayIndex ?? activeIndex) + 1 }}
    </div>

    <div class="feed-picker-slider__item-inner">
      <div class="feed-picker-slider__item-head">
        <h2 :class="`feed-picker-slider__item-title ${item.value}`">
          {{ item.title }}
        </h2>

        <div
            v-if="item.description"
            class="feed-picker-slider__item-description"
            v-html="item.description"
            @click="handleDescriptionClick"
        />
      </div>

      <div :class="`feed-picker-slider__options ${item.value}`">
        <div
            v-for="(option, idx) in filteredOptions"
            :key="`${item.value}-${idx}-${option.__value}`"
            class="feed-picker-slider__options-item"
            :class="{
              'checkbox--skip': option.__value === NONE_NEED,
              'checkbox--skip-sm':
              item.value === 'textures' &&
              option.__value === NONE_NEED &&
              filteredOptions.length <= 3
            }"
        >
          <RadioButton
              v-if="item.type === 'radio'"
              :name="item.title"
              :text="option.name"
              :value="option.__value"
              :img="option.img"
              :variant="item.style_variant"
              :model-value="props.modelValue"
              @update:modelValue="handleRadioChange"
          />

          <CheckBox
              v-else-if="item.type === 'checkbox'"
              :name="item.title"
              :text="option.name"
              :value="option.__value"
              :img="option.img"
              v-model="checkboxModel"
          />
        </div>
      </div>

      <div
          v-if="showContinueButton"
          class="feed-picker-slider__next-btn"
          :class="{ '_disabled': continueDisabled }"
          @click="onContinue"
      >
        {{ props.isLastStep ? 'Подобрать корм' : 'Продолжить' }}
      </div>
    </div>
  </div>
</template>
